import { debounce } from '@/entrypoints/utils';
import { Cart, CartEvents, TypedCartEvent, CartRewardTier } from '../internals/core';
import { CartEventBus } from '../internals/event-bus';
import { MiniCart } from './mini-cart';
import { createLogger } from '@/lib/logger';

class MiniCartProgressBar extends HTMLElement {
  private abortController: AbortController | null = null;
  private eventBus: CartEventBus | null = null;
  private logger = createLogger('mini-cart-progress-bar');

  private numberFormatter = new Intl.NumberFormat('en-AU', {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  private encouragementMessageElem: HTMLElement | null = null;
  private rewardTiers: CartRewardTier[] = [];
  private progressBarFillElem: HTMLElement | null = null;

  constructor() {
    super();
  }

  // Add internal logic here as separated functions
  private onCartChanged(e: TypedCartEvent<'cart:changed:debounced'>) {
    this.logger.info('Cart changed', e.detail);
    const { cart } = e.detail;
    this.render(cart);
  }

  private debouncedOnCartChanged = debounce(this.onCartChanged.bind(this), 200);

  private render(cart: Cart) {
    const cartContainsMedispaService = !!cart.items.find((item) =>
      item.vendor.toLowerCase().includes('medispa'),
    );

    this.classList.toggle('!hidden', cartContainsMedispaService);

    const subtotalExcludingGifts = cart.items.reduce((acc, item) => {
      if (!item.properties?.['__free_gift']) {
        return acc + (item.final_line_price ? item.final_line_price / 100 : 0);
      }
      return acc;
    }, 0);


    this.logger.info('Subtotal excluding gifts', subtotalExcludingGifts);

    let nextTier: CartRewardTier | undefined;

    this.rewardTiers.forEach((tier, index) => {
      if (tier.thresholdAmount > subtotalExcludingGifts && !nextTier) {
        nextTier = tier;
      }

      const unlocked = tier.thresholdAmount <= subtotalExcludingGifts;

      tier.element
        .querySelector('.mini-cart__progress-step-icons')
        ?.setAttribute('data-unlocked', unlocked ? 'true' : 'false');

      if (unlocked) {
        const percentages = [25, 55, 75, 100];
        this.progressBarFillElem?.setAttribute('style', `width: ${percentages[index]}%`);
      }
    });

    this.logger.info('Next tier', nextTier);

    if (nextTier?.thresholdAmount === this.rewardTiers[0].thresholdAmount) {
      this.progressBarFillElem?.setAttribute('style', `width: 0%`);
    }

    const allGiftsUnlocked =
      subtotalExcludingGifts >= this.rewardTiers[this.rewardTiers.length - 1].thresholdAmount;
    this.logger.info('All gifts unlocked', allGiftsUnlocked);

    if (allGiftsUnlocked && this.encouragementMessageElem) {
      this.encouragementMessageElem.innerHTML =
        this.encouragementMessageElem.getAttribute('data-all-gifts-unlocked-message') ?? '';
    }

    if (nextTier && this.encouragementMessageElem) {
      this.encouragementMessageElem.innerHTML = nextTier.encouragementMessage // @ts-ignore
        .replaceAll(
          '{remaining_amount}',
          `${nextTier.currencySymbol}${this.numberFormatter.format(nextTier.thresholdAmount - subtotalExcludingGifts)}`,
        )
        .replaceAll('{threshold_money}', nextTier.thresholdDisplayAmount);
    }
  }

  private bindEvents() {
    this.eventBus!.on(CartEvents.DebouncedChange, this.debouncedOnCartChanged.bind(this));
    this.eventBus!.on(CartEvents.AddSuccess, this.debouncedOnCartChanged.bind(this));
  }

  private unbindEvents() {
    this.eventBus!.off(CartEvents.DebouncedChange, this.debouncedOnCartChanged.bind(this));
    this.eventBus!.off(CartEvents.AddSuccess, this.debouncedOnCartChanged.bind(this));
  }

  private onMiniCartReady(parentMiniCart: MiniCart) {
    // Make sure we use the same event bus as the parent mini-cart.
    this.eventBus = parentMiniCart.bus;

    this.bindEvents();

    window.Shopify.getCart().then((cart) => {
      if (cart) {
        // @ts-ignore
        this.render(cart);
      }
    });
  }

  // Lifecycle hooks
  connectedCallback() {
    this.abortController = new AbortController();

    const parentMiniCart = this.closest('mini-cart')! as MiniCart;
    if (!parentMiniCart) {
      this.logger.error('MiniCartHeader must be a child of a MiniCart element');
      throw new Error('MiniCartHeader must be a child of a MiniCart element');
    }

    parentMiniCart.execOnReady(this.onMiniCartReady.bind(this, parentMiniCart));

    this.encouragementMessageElem = this.querySelector(
      '[data-progress-title]',
    ) as HTMLElement | null;

    this.querySelectorAll('.mini-cart__progress-step').forEach((step) => {
      const currencyRate = parseFloat(window.Shopify.currency.rate || '1');
      const encouragementMessage = step.getAttribute('data-encouragement-message');
      const thresholdAmount = parseFloat(step.getAttribute('data-threshold') || '0') * (currencyRate);
      const currencySymbol = step.getAttribute('data-currency-symbol') || '$';
      const thresholdDisplayAmount = `${currencySymbol}${thresholdAmount.toFixed(0)}`;

      const displayElement = step.querySelector('[data-threshhold-amount]');
      if (displayElement) {
        displayElement.textContent = thresholdDisplayAmount;
      }

      this.rewardTiers.push({
        currencySymbol: currencySymbol,
        encouragementMessage: encouragementMessage ?? '',
        thresholdAmount: thresholdAmount ? thresholdAmount : 0,
        thresholdDisplayAmount: thresholdDisplayAmount ?? '',
        element: step as HTMLElement,
      });
    });

    this.progressBarFillElem = this.querySelector('[data-progress-bar-fill]') as HTMLElement | null;
  }

  disconnectedCallback() {
    this.abortController?.abort();

    this.unbindEvents();
  }
}

if (!customElements.get('mini-cart-progress-bar')) {
  customElements.define('mini-cart-progress-bar', MiniCartProgressBar);
}